/**
 * Stand-in for the E1 endpoints until they exist in the OpenAPI contract.
 *
 * Every function here returns exactly what its future endpoint is planned to
 * return, with the same latency shape (a job the UI polls, a mutation that
 * invalidates). Screens import from `@/api` and never from this folder
 * directly, so replacing it with the generated client is a one-file change.
 *
 * It decides nothing. Which sample response comes back is chosen explicitly by
 * the person using the demo — no rule is simulated here that the backend owns.
 */

import {
  SAMPLE_CASE,
  SAMPLE_CASE_REJECTED,
  SAMPLE_CASE_SPECIAL,
  SAMPLE_EXTRACTION,
  SAMPLE_PLAN,
} from "./fixtures";
import type { CasePlan, CaseSummary, ExtractionRun, ExtractedValue } from "./types";

/** Which sample intake response the demo should return. */
export type PreviewScenario = "valid" | "special_case" | "rejected";

const CASES: Record<PreviewScenario, CaseSummary> = {
  valid: SAMPLE_CASE,
  special_case: SAMPLE_CASE_SPECIAL,
  rejected: SAMPLE_CASE_REJECTED,
};

const LATENCY_MS = 420;

function delay<T>(value: T, ms = LATENCY_MS): Promise<T> {
  return new Promise((resolve) => {
    setTimeout(() => resolve(value), ms);
  });
}

/** Mutable copy so confirmations survive within a session. */
let extractionState: ExtractionRun = structuredClone(SAMPLE_EXTRACTION);

export function resetPreviewState(): void {
  extractionState = structuredClone(SAMPLE_EXTRACTION);
}

export function getCase(scenario: PreviewScenario): Promise<CaseSummary> {
  return delay(CASES[scenario]);
}

export function getExtraction(): Promise<ExtractionRun> {
  return delay(structuredClone(extractionState));
}

export function getPlan(): Promise<CasePlan> {
  return delay(structuredClone(SAMPLE_PLAN));
}

export interface ConfirmValueInput {
  valueId: string;
  /** Present when the person corrected the reading instead of confirming it. */
  correctedValueMm?: number | null;
  correctedValueText?: string | null;
  confirmedBy: string;
}

/**
 * A-014: a value becomes production-relevant only through this call.
 * The backend will re-run the closure check and re-plan; here the value simply
 * flips to `confirmed` and keeps its original reading visible.
 */
export function confirmValue(input: ConfirmValueInput): Promise<ExtractedValue> {
  const value = extractionState.values.find((candidate) => candidate.id === input.valueId);
  if (!value) {
    return Promise.reject(new Error(`Unbekannter Wert: ${input.valueId}`));
  }

  const corrected =
    input.correctedValueMm !== undefined || input.correctedValueText !== undefined;

  const updated: ExtractedValue = {
    ...value,
    valueMm: input.correctedValueMm !== undefined ? input.correctedValueMm : value.valueMm,
    valueText:
      input.correctedValueText !== undefined ? input.correctedValueText : value.valueText,
    status: "confirmed",
    provenance: corrected ? "manual" : "confirmed",
    confirmedBy: input.confirmedBy,
    confirmedAt: new Date().toISOString(),
    note: corrected ? "Korrigiert und bestätigt." : value.note,
  };

  extractionState = {
    ...extractionState,
    values: extractionState.values.map((candidate) =>
      candidate.id === updated.id ? updated : candidate,
    ),
  };

  return delay(structuredClone(updated), 260);
}
